import { Bookmark } from 'lucide-react';
import { MouseEvent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import {
  useCreateProjectGroupBookmarkMutation,
  useDeleteProjectGroupBookmarkMutation,
} from './bookmark.api.slice';
import { setShowLoginDialog } from '../global/global.slice';
import { UserState } from '../auth/auth.d';

function ProjectGroupBookmarkButton({
  bookmarked,
  projectGroupId,
  size = 'sm',
}: {
  bookmarked: boolean | undefined;
  projectGroupId: string;
  size?: 'sm' | 'md' | 'lg' | 'icon';
}) {
  const dispatch = useDispatch();
  const { user } = useSelector((state: { auth: UserState }) => state.auth);
  const [createBookmark] = useCreateProjectGroupBookmarkMutation();
  const [deleteBookmark] = useDeleteProjectGroupBookmarkMutation();

  // Optimistic local state: reflect the click immediately instead of waiting
  // for the server round-trip / cache refetch. Re-synced from props whenever
  // the underlying query refetches.
  const [isBookmarked, setIsBookmarked] = useState(bookmarked ?? false);
  useEffect(() => {
    setIsBookmarked(bookmarked ?? false);
  }, [bookmarked]);

  const handleToggle = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!user) {
      dispatch(setShowLoginDialog(true));
      return;
    }

    const wasBookmarked = isBookmarked;
    setIsBookmarked(!wasBookmarked);

    try {
      if (wasBookmarked) {
        await deleteBookmark(projectGroupId).unwrap();
      } else {
        await createBookmark(projectGroupId).unwrap();
      }
    } catch {
      // Revert on failure
      setIsBookmarked(wasBookmarked);
    }
  };

  if (isBookmarked) {
    return (
      <Button
        variant="secondary"
        size={size}
        className="flex items-center gap-1 justify-center border-gray-400 border-[1.5px] text-black text-[13px] bg-white hover:bg-white"
        // Icon mode drops the label; this stays the accessible name.
        aria-label="Bookmarked"
        title="Remove bookmark"
        onClick={handleToggle}
      >
        <Bookmark
          fill="#292929"
          stroke="#292929"
          size={size === 'icon' ? 20 : 16}
        />
        {size !== 'icon' && 'Bookmarked'}
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size={size}
      aria-label="Bookmark"
      title="Bookmark"
      onClick={handleToggle}
      className="flex items-center gap-1 justify-center text-[13px] border-gray-400 border-[1.5px]"
    >
      <Bookmark stroke="#292929" size={size === 'icon' ? 20 : 16} />
      {size !== 'icon' && 'Bookmark'}
    </Button>
  );
}

export default ProjectGroupBookmarkButton;
